import {
  Envelope, Lightning, Clock, Target,
  Presentation, Flask,
  ListBullets,
  type Icon,
} from '@phosphor-icons/react'
// LinkedInBrandIcon: only used by the hidden multiChannelOutreach template below.
// import { LinkedInBrandIcon } from '@/components/icons/LinkedInBrandIcon'
import type { WorkflowNode, WorkflowEdge, EmailTone, EmailPurpose } from '@/types/workflow'

export type WorkflowTemplateCategory =
  | 'cold_outreach'
  | 'follow_up'
  | 'nurture'
  | 'lead_qualification'
  | 'event_driven'
  | 'multi_channel'

export interface WorkflowTemplateDef {
  id: string
  name: string
  description: string
  category: WorkflowTemplateCategory
  icon: Icon
  tags: string[]
  flowSummary: string
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]
}

export const WORKFLOW_CATEGORY_LABELS: Record<WorkflowTemplateCategory, string> = {
  cold_outreach: 'Cold Outreach',
  follow_up: 'Follow-Up',
  nurture: 'Nurture',
  lead_qualification: 'Lead Qualification',
  event_driven: 'Event-Driven',
  multi_channel: 'Multi-Channel',
}

// Editorial Dark §11 anti-pattern: no new chromatic colors. All category
// chips share a single neutral pill style — differentiation lives in the
// label text. Keeps the route indigo-dominant + amber-as-single-accent.
const NEUTRAL_CHIP =
  'bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98]'

export const WORKFLOW_CATEGORY_COLORS: Record<WorkflowTemplateCategory, string> = {
  cold_outreach: NEUTRAL_CHIP,
  follow_up: NEUTRAL_CHIP,
  nurture: NEUTRAL_CHIP,
  lead_qualification: NEUTRAL_CHIP,
  event_driven: NEUTRAL_CHIP,
  multi_channel: NEUTRAL_CHIP,
}

// Helper to generate unique IDs per template
let _c = 0
const id = (prefix: string) => `${prefix}_${++_c}`

const aiEmailConfig = (purpose: EmailPurpose, tone: EmailTone = 'professional') => ({
  generation_mode: 'ai' as const,
  material_ids: [],
  contact_fields: [
    'job_title', 'company_name', 'company_industry',
    'outreach_hooks', 'buying_signals', 'communication_prefs',
    'person_enrichment', 'company_enrichment',
  ] as any[],
  tone,
  purpose,
  custom_instructions: '',
})

// ─── Template 1: Cold Outreach — 3-Step ──────────────────────────────
function coldOutreach3Step(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const enrich = id('n')
  const email1 = id('n')
  const delay1 = id('n')
  const cond = id('n')
  const email2 = id('n')
  const delay2 = id('n')
  const email3 = id('n')

  return {
    id: 'cold_outreach_3step',
    name: 'Cold Outreach — 3-Step',
    description: 'A proven 3-email cold outreach sequence with smart follow-up. Sends an initial email, checks if it was opened, and follows up with a value-add or breakup email accordingly. Best for B2B prospecting.',
    category: 'cold_outreach',
    icon: Envelope,
    tags: ['prospecting', 'email sequence', 'automated'],
    flowSummary: 'Trigger → Enrich → Email → Delay → Condition → Follow-up / Breakup',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'Start Campaign', trigger_type: 'manual' } },
      { id: enrich, type: 'enrichContacts', position: { x: 250, y: 150 }, data: { label: 'Enrich Leads', enrich_contacts_config: { skip_already_enriched: true, max_age_days: 0, enabled_blocks: ['person', 'company', 'outreach_hooks', 'buying_signals', 'communication_prefs'], providers: [], execution_mode: 'parallel' } } },
      { id: email1, type: 'sendEmail', position: { x: 250, y: 300 }, data: { label: 'Initial Outreach', subject: '', body: '', ai_config: aiEmailConfig('introduction') } },
      { id: delay1, type: 'delay', position: { x: 250, y: 450 }, data: { label: 'Wait 3 Days', delay_value: 3, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: cond, type: 'condition', position: { x: 250, y: 600 }, data: { label: 'Email Opened?', condition_type: 'email_opened' } },
      { id: email2, type: 'sendEmail', position: { x: 0, y: 750 }, data: { label: 'Value Follow-Up', subject: '', body: '', ai_config: aiEmailConfig('follow_up') } },
      { id: delay2, type: 'delay', position: { x: 500, y: 750 }, data: { label: 'Wait 2 Days', delay_value: 2, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: email3, type: 'sendEmail', position: { x: 500, y: 900 }, data: { label: 'Breakup Email', subject: '', body: '', ai_config: aiEmailConfig('re_engage', 'friendly') } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: enrich, animated: true, type: 'smoothstep' },
      { id: 'e2', source: enrich, target: email1, animated: true, type: 'smoothstep' },
      { id: 'e3', source: email1, target: delay1, animated: true, type: 'smoothstep' },
      { id: 'e4', source: delay1, target: cond, animated: true, type: 'smoothstep' },
      { id: 'e5', source: cond, target: email2, sourceHandle: 'yes', animated: true, type: 'smoothstep' },
      { id: 'e6', source: cond, target: delay2, sourceHandle: 'no', animated: true, type: 'smoothstep' },
      { id: 'e8', source: delay2, target: email3, animated: true, type: 'smoothstep' },
    ],
  }
}

// ─── Template 2: Warm Follow-Up ───────────────────────────────────────
function warmFollowUp(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const email1 = id('n')
  const delay1 = id('n')
  const cond = id('n')
  const email2 = id('n')
  const delay2 = id('n')
  const exit = id('n')

  return {
    id: 'warm_follow_up',
    name: 'Warm Follow-Up',
    description: 'Follow up after an initial meeting or conversation. Sends a thank-you email, waits for a reply, and nudges if no response. Ideal for post-meeting or post-demo sequences.',
    category: 'follow_up',
    icon: Envelope,
    tags: ['post-meeting', 'thank you', 'gentle nudge'],
    flowSummary: 'Trigger → Thank You Email → Delay → Replied? → Nudge → Exit',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'After Meeting', trigger_type: 'manual' } },
      { id: email1, type: 'sendEmail', position: { x: 250, y: 150 }, data: { label: 'Thank You Email', subject: '', body: '', ai_config: aiEmailConfig('thank_you', 'friendly') } },
      { id: delay1, type: 'delay', position: { x: 250, y: 300 }, data: { label: 'Wait 2 Days', delay_value: 2, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: cond, type: 'condition', position: { x: 250, y: 450 }, data: { label: 'Got Reply?', condition_type: 'email_replied' } },
      { id: email2, type: 'sendEmail', position: { x: 500, y: 600 }, data: { label: 'Gentle Nudge', subject: '', body: '', ai_config: aiEmailConfig('follow_up', 'friendly') } },
      { id: delay2, type: 'delay', position: { x: 500, y: 750 }, data: { label: 'Wait 3 Days', delay_value: 3, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: exit, type: 'exitWorkflow', position: { x: 500, y: 900 }, data: { label: 'End Sequence', exit_workflow_config: { reason: 'custom', custom_reason: 'No reply after follow-up' } } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: email1, animated: true, type: 'smoothstep' },
      { id: 'e2', source: email1, target: delay1, animated: true, type: 'smoothstep' },
      { id: 'e3', source: delay1, target: cond, animated: true, type: 'smoothstep' },
      { id: 'e5', source: cond, target: email2, sourceHandle: 'no', animated: true, type: 'smoothstep' },
      { id: 'e6', source: email2, target: delay2, animated: true, type: 'smoothstep' },
      { id: 'e7', source: delay2, target: exit, animated: true, type: 'smoothstep' },
    ],
  }
}

// ─── Template 3: Lead Qualification & Routing ─────────────────────────
function leadQualificationRouting(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const enrich = id('n')
  const branch = id('n')
  const assign = id('n')
  const task = id('n')
  const alert = id('n')
  const crm = id('n')
  const email = id('n')
  const exit = id('n')

  return {
    id: 'lead_qualification_routing',
    name: 'Lead Qualification & Routing',
    description: 'Automatically enrich leads, score them by seniority level, and route to the right action. C-level prospects get assigned to a rep with a follow-up task, synced to CRM with an alert. Managers get a nurture email, and others are exited.',
    category: 'lead_qualification',
    icon: ListBullets,
    tags: ['scoring', 'routing', 'qualification', 'CRM'],
    flowSummary: 'Trigger → Enrich → Branch(seniority) → Task+Assign+CRM+Alert / Email / Exit',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'New Lead', trigger_type: 'contact_added' } },
      { id: enrich, type: 'enrichContacts', position: { x: 250, y: 150 }, data: { label: 'Enrich Lead', enrich_contacts_config: { skip_already_enriched: true, max_age_days: 0, enabled_blocks: ['person', 'company', 'buying_signals'], providers: [], execution_mode: 'parallel' } } },
      { id: branch, type: 'branchByField', position: { x: 250, y: 300 }, data: { label: 'Check Seniority', branch_by_field_config: { branches: [{ id: 'exec', label: 'Executive', field: 'seniority_level', operator: 'equals', value: 'c_suite' }, { id: 'mgr', label: 'Manager', field: 'seniority_level', operator: 'equals', value: 'manager' }], has_default: true } } },
      { id: task, type: 'createTask', position: { x: 0, y: 480 }, data: { label: 'Representative Follow-Up Task', create_task_config: { task_type: 'call', title_template: 'Call {{contact.full_name}} ({{contact.job_title}} at {{contact.company_name}})', description_template: 'Executive-level lead assigned to you. Reach out within one business day.', priority: 'high', due_in_days: 1 } } },
      { id: assign, type: 'assignOwner', position: { x: 0, y: 630 }, data: { label: 'Assign to Representative', assign_owner_config: { team_member_emails: [], assignment_mode: 'round_robin' } } },
      { id: crm, type: 'crmSync', position: { x: 0, y: 780 }, data: { label: 'Push to CRM', crm_sync_config: { provider: 'hubspot', sync_contact: true, create_deal: true, deal_name_template: '{{contact.company_name}} — Inbound Lead', deal_stage: 'Qualified', field_mapping: {} } } },
      { id: alert, type: 'internalAlert', position: { x: 0, y: 930 }, data: { label: 'Notify Sales', internal_alert_config: { channel: 'email', message_template: 'High-value lead: {{contact.full_name}} ({{contact.job_title}} at {{contact.company_name}})', include_contact_details: true } } },
      { id: email, type: 'sendEmail', position: { x: 250, y: 480 }, data: { label: 'Nurture Email', subject: '', body: '', ai_config: aiEmailConfig('nurture') } },
      { id: exit, type: 'exitWorkflow', position: { x: 500, y: 480 }, data: { label: 'Not Qualified', exit_workflow_config: { reason: 'wrong_persona', add_tag: 'low_priority' } } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: enrich, animated: true, type: 'smoothstep' },
      { id: 'e2', source: enrich, target: branch, animated: true, type: 'smoothstep' },
      { id: 'e3', source: branch, target: task, sourceHandle: 'exec', animated: true, type: 'smoothstep' },
      { id: 'e4', source: task, target: assign, animated: true, type: 'smoothstep' },
      { id: 'e5', source: assign, target: crm, animated: true, type: 'smoothstep' },
      { id: 'e6', source: crm, target: alert, animated: true, type: 'smoothstep' },
      { id: 'e7', source: branch, target: email, sourceHandle: 'mgr', animated: true, type: 'smoothstep' },
      { id: 'e8', source: branch, target: exit, sourceHandle: 'default', animated: true, type: 'smoothstep' },
    ],
  }
}

// ─── Template 4: Nurture Drip — 5-Touch ──────────────────────────────
function nurtureDrip5Touch(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const e1 = id('n'), d1 = id('n')
  const e2 = id('n'), d2 = id('n')
  const e3 = id('n'), d3 = id('n')
  const e4 = id('n'), d4 = id('n')
  const e5 = id('n')

  return {
    id: 'nurture_drip_5touch',
    name: 'Nurture Drip — 5-Touch',
    description: 'A long-term nurture sequence that keeps your brand top-of-mind. Five value-driven emails spread over 4 weeks, each building on the last. Perfect for leads that aren\'t ready to buy yet.',
    category: 'nurture',
    icon: Clock,
    tags: ['drip', 'long-term', 'value-driven', '5 emails'],
    flowSummary: 'Trigger → Email → Delay → Email → Delay → ... (5x)',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'Start Drip', trigger_type: 'manual' } },
      { id: e1, type: 'sendEmail', position: { x: 250, y: 150 }, data: { label: 'Email 1: Introduction', subject: '', body: '', ai_config: aiEmailConfig('introduction') } },
      { id: d1, type: 'delay', position: { x: 250, y: 300 }, data: { label: 'Wait 5 Days', delay_value: 5, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: e2, type: 'sendEmail', position: { x: 250, y: 450 }, data: { label: 'Email 2: Value Add', subject: '', body: '', ai_config: aiEmailConfig('nurture') } },
      { id: d2, type: 'delay', position: { x: 250, y: 600 }, data: { label: 'Wait 5 Days', delay_value: 5, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: e3, type: 'sendEmail', position: { x: 250, y: 750 }, data: { label: 'Email 3: Case Study', subject: '', body: '', ai_config: aiEmailConfig('nurture') } },
      { id: d3, type: 'delay', position: { x: 250, y: 900 }, data: { label: 'Wait 7 Days', delay_value: 7, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: e4, type: 'sendEmail', position: { x: 250, y: 1050 }, data: { label: 'Email 4: Social Proof', subject: '', body: '', ai_config: aiEmailConfig('nurture') } },
      { id: d4, type: 'delay', position: { x: 250, y: 1200 }, data: { label: 'Wait 7 Days', delay_value: 7, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: e5, type: 'sendEmail', position: { x: 250, y: 1350 }, data: { label: 'Email 5: Soft Ask', subject: '', body: '', ai_config: aiEmailConfig('pitch', 'friendly') } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: e1, animated: true, type: 'smoothstep' },
      { id: 'e2', source: e1, target: d1, animated: true, type: 'smoothstep' },
      { id: 'e3', source: d1, target: e2, animated: true, type: 'smoothstep' },
      { id: 'e4', source: e2, target: d2, animated: true, type: 'smoothstep' },
      { id: 'e5', source: d2, target: e3, animated: true, type: 'smoothstep' },
      { id: 'e6', source: e3, target: d3, animated: true, type: 'smoothstep' },
      { id: 'e7', source: d3, target: e4, animated: true, type: 'smoothstep' },
      { id: 'e8', source: e4, target: d4, animated: true, type: 'smoothstep' },
      { id: 'e9', source: d4, target: e5, animated: true, type: 'smoothstep' },
    ],
  }
}

// ─── Template 5: Event/Webinar Invite ─────────────────────────────────
function eventWebinarInvite(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const email1 = id('n')
  const delay1 = id('n')
  const cond = id('n')
  const emailConfirm = id('n')
  const emailReminder = id('n')
  const delay2 = id('n')
  const sms = id('n')

  return {
    id: 'event_webinar_invite',
    name: 'Event / Webinar Invite',
    description: 'Drive registrations with a multi-touch invite flow. Sends an invite email, follows up with a confirmation if they clicked, or sends a reminder + SMS for those who didn\'t engage. Great for webinars, events, or product launches.',
    category: 'event_driven',
    icon: Target,
    tags: ['webinar', 'event', 'invite', 'multi-touch', 'SMS'],
    flowSummary: 'Trigger → Invite → Delay → Clicked? → Confirm / Reminder + SMS',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'Launch Invite', trigger_type: 'manual' } },
      { id: email1, type: 'sendEmail', position: { x: 250, y: 150 }, data: { label: 'Event Invite', subject: '', body: '', ai_config: aiEmailConfig('introduction', 'persuasive') } },
      { id: delay1, type: 'delay', position: { x: 250, y: 300 }, data: { label: 'Wait 3 Days', delay_value: 3, delay_unit: 'days', delay_mode: 'duration' } },
      { id: cond, type: 'condition', position: { x: 250, y: 450 }, data: { label: 'Clicked Link?', condition_type: 'email_clicked' } },
      { id: emailConfirm, type: 'sendEmail', position: { x: 0, y: 600 }, data: { label: 'Confirmation', subject: '', body: '', ai_config: aiEmailConfig('follow_up', 'friendly') } },
      { id: emailReminder, type: 'sendEmail', position: { x: 500, y: 600 }, data: { label: 'Reminder', subject: '', body: '', ai_config: aiEmailConfig('follow_up', 'urgent') } },
      { id: delay2, type: 'delay', position: { x: 500, y: 750 }, data: { label: 'Wait 1 Day', delay_value: 1, delay_unit: 'days', delay_mode: 'duration' } },
      { id: sms, type: 'smsMessage', position: { x: 500, y: 900 }, data: { label: 'SMS Reminder', sms_message_config: { message_template: 'Hi {{contact.first_name}}, just a quick reminder about our upcoming event. Don\'t miss it!' } } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: email1, animated: true, type: 'smoothstep' },
      { id: 'e2', source: email1, target: delay1, animated: true, type: 'smoothstep' },
      { id: 'e3', source: delay1, target: cond, animated: true, type: 'smoothstep' },
      { id: 'e4', source: cond, target: emailConfirm, sourceHandle: 'yes', animated: true, type: 'smoothstep' },
      { id: 'e5', source: cond, target: emailReminder, sourceHandle: 'no', animated: true, type: 'smoothstep' },
      { id: 'e6', source: emailReminder, target: delay2, animated: true, type: 'smoothstep' },
      { id: 'e7', source: delay2, target: sms, animated: true, type: 'smoothstep' },
    ],
  }
}

// ─── Template 6: Multi-Channel Outreach ───────────────────────────────
// TODO: hidden for now — feature not built yet, revisit in future
/*
function multiChannelOutreach(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const enrich = id('n')
  const email = id('n')
  const delay1 = id('n')
  const linkedin = id('n')
  const delay2 = id('n')
  const cond = id('n')
  const goal = id('n')
  const sms = id('n')
  const delay3 = id('n')
  const breakup = id('n')

  return {
    id: 'multi_channel_outreach',
    name: 'Multi-Channel Outreach',
    description: 'Reach prospects across email, LinkedIn, and SMS. Starts with email, adds a LinkedIn connection request, then checks for engagement. If no reply, follows up with SMS and a final breakup email.',
    category: 'multi_channel',
    icon: LinkedInBrandIcon,
    tags: ['email', 'linkedin', 'sms', 'omnichannel'],
    flowSummary: 'Trigger → Enrich → Email → LinkedIn → Condition → Goal / SMS → Breakup',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'Start Outreach', trigger_type: 'manual' } },
      { id: enrich, type: 'enrichContacts', position: { x: 250, y: 150 }, data: { label: 'Enrich', enrich_contacts_config: { skip_already_enriched: true, max_age_days: 0, enabled_blocks: ['person', 'company', 'outreach_hooks', 'communication_prefs'], providers: [], execution_mode: 'parallel' } } },
      { id: email, type: 'sendEmail', position: { x: 250, y: 300 }, data: { label: 'Opening Email', subject: '', body: '', ai_config: aiEmailConfig('introduction') } },
      { id: delay1, type: 'delay', position: { x: 250, y: 450 }, data: { label: 'Wait 2 Days', delay_value: 2, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: linkedin, type: 'linkedInAction', position: { x: 250, y: 600 }, data: { label: 'LinkedIn Connect', linkedin_action_config: { action_type: 'connect', message_template: 'Hi {{contact.first_name}}, I came across your work at {{contact.company_name}} and would love to connect.' } } },
      { id: delay2, type: 'delay', position: { x: 250, y: 750 }, data: { label: 'Wait 3 Days', delay_value: 3, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: cond, type: 'condition', position: { x: 250, y: 900 }, data: { label: 'Got Reply?', condition_type: 'email_replied' } },
      { id: goal, type: 'goal', position: { x: 0, y: 1050 }, data: { label: 'Engaged', goal_config: { goal_type: 'replied' } } },
      { id: sms, type: 'smsMessage', position: { x: 500, y: 1050 }, data: { label: 'SMS Touch', sms_message_config: { message_template: 'Hi {{contact.first_name}}, I sent you an email about {{contact.company_name}} — would love to chat briefly. Reply here or I\'ll send one more email.' } } },
      { id: delay3, type: 'delay', position: { x: 500, y: 1200 }, data: { label: 'Wait 2 Days', delay_value: 2, delay_unit: 'days', delay_mode: 'duration' } },
      { id: breakup, type: 'sendEmail', position: { x: 500, y: 1350 }, data: { label: 'Breakup Email', subject: '', body: '', ai_config: aiEmailConfig('re_engage', 'friendly') } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: enrich, animated: true, type: 'smoothstep' },
      { id: 'e2', source: enrich, target: email, animated: true, type: 'smoothstep' },
      { id: 'e3', source: email, target: delay1, animated: true, type: 'smoothstep' },
      { id: 'e4', source: delay1, target: linkedin, animated: true, type: 'smoothstep' },
      { id: 'e5', source: linkedin, target: delay2, animated: true, type: 'smoothstep' },
      { id: 'e6', source: delay2, target: cond, animated: true, type: 'smoothstep' },
      { id: 'e7', source: cond, target: goal, sourceHandle: 'yes', animated: true, type: 'smoothstep' },
      { id: 'e8', source: cond, target: sms, sourceHandle: 'no', animated: true, type: 'smoothstep' },
      { id: 'e9', source: sms, target: delay3, animated: true, type: 'smoothstep' },
      { id: 'e10', source: delay3, target: breakup, animated: true, type: 'smoothstep' },
    ],
  }
}
*/

// ─── Template 7: A/B Test Subject Lines ──────────────────────────────
function abTestSubjectLines(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const ab = id('n')
  const emailA = id('n')
  const emailB = id('n')
  const delay = id('n')
  const cond = id('n')
  const followup = id('n')

  return {
    id: 'ab_test_subject_lines',
    name: 'A/B Test Subject Lines',
    description: 'Test two different email approaches to find what resonates. Splits your audience 50/50, sends each variant, then measures opens and replies. Use this to optimize your messaging before scaling.',
    category: 'cold_outreach',
    icon: Flask,
    tags: ['split test', 'optimization', 'data-driven'],
    flowSummary: 'Trigger → A/B Split → Email A / Email B → Delay → Opened? → Follow-up',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'Start Test', trigger_type: 'manual' } },
      { id: ab, type: 'abTest', position: { x: 250, y: 150 }, data: { label: 'Split 50/50', ab_test_config: { variants: [{ id: 'variant_1', label: 'Variant 1', percentage: 50 }, { id: 'variant_2', label: 'Variant 2', percentage: 50 }] } } },
      { id: emailA, type: 'sendEmail', position: { x: 0, y: 330 }, data: { label: 'Variant A: Direct', subject: '', body: '', ai_config: aiEmailConfig('introduction', 'professional') } },
      { id: emailB, type: 'sendEmail', position: { x: 500, y: 330 }, data: { label: 'Variant B: Casual', subject: '', body: '', ai_config: aiEmailConfig('introduction', 'casual') } },
      { id: delay, type: 'delay', position: { x: 250, y: 510 }, data: { label: 'Wait 3 Days', delay_value: 3, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: cond, type: 'condition', position: { x: 250, y: 660 }, data: { label: 'Email Opened?', condition_type: 'email_opened' } },
      { id: followup, type: 'sendEmail', position: { x: 500, y: 810 }, data: { label: 'Follow-Up', subject: '', body: '', ai_config: aiEmailConfig('follow_up') } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: ab, animated: true, type: 'smoothstep' },
      { id: 'e2', source: ab, target: emailA, sourceHandle: 'variant_1', animated: true, type: 'smoothstep' },
      { id: 'e3', source: ab, target: emailB, sourceHandle: 'variant_2', animated: true, type: 'smoothstep' },
      { id: 'e4', source: emailA, target: delay, animated: true, type: 'smoothstep' },
      { id: 'e5', source: emailB, target: delay, animated: true, type: 'smoothstep' },
      { id: 'e6', source: delay, target: cond, animated: true, type: 'smoothstep' },
      { id: 'e8', source: cond, target: followup, sourceHandle: 'no', animated: true, type: 'smoothstep' },
    ],
  }
}

// ─── Template 8: Enrichment → Personalized Deck ──────────────────────
function enrichmentPersonalizedDeck(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const enrich = id('n')
  const deck = id('n')
  const email = id('n')
  const delay = id('n')
  const cond = id('n')
  const task = id('n')
  const followup = id('n')

  return {
    id: 'enrichment_personalized_deck',
    name: 'Enrichment + Personalized Deck',
    description: 'The premium outreach workflow. Enriches leads, generates a personalized slide deck using AI, sends it via email, and creates a sales task if they engage. Perfect for high-value enterprise prospects.',
    category: 'lead_qualification',
    icon: Presentation,
    tags: ['enterprise', 'deck', 'personalized', 'high-touch'],
    flowSummary: 'Trigger → Enrich → Generate Deck → Email + Deck → Delay → Opened? → Task / Follow-up',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'High-Value Lead', trigger_type: 'manual' } },
      { id: enrich, type: 'enrichContacts', position: { x: 250, y: 150 }, data: { label: 'Deep Enrich', enrich_contacts_config: { skip_already_enriched: true, max_age_days: 0, enabled_blocks: ['person', 'company', 'sector', 'economy', 'outreach_hooks', 'buying_signals', 'competitive_intel', 'communication_prefs'], providers: [], execution_mode: 'parallel' } } },
      { id: deck, type: 'generateDeck', position: { x: 250, y: 300 }, data: { label: 'Generate Deck', generate_deck_config: { mode: 'ai_generate', material_ids: [], contact_fields: ['job_title', 'company_name', 'company_industry', 'person_enrichment', 'company_enrichment', 'sector_enrichment', 'competitive_intel'], slide_count: 8, content_focus: 'pitch_deck', tone: 'professional', custom_instructions: '', color_theme: 'auto_from_brand' } } },
      { id: email, type: 'sendEmail', position: { x: 250, y: 450 }, data: { label: 'Send with Deck', subject: '', body: '', ai_config: aiEmailConfig('pitch') } },
      { id: delay, type: 'delay', position: { x: 250, y: 600 }, data: { label: 'Wait 3 Days', delay_value: 3, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: cond, type: 'condition', position: { x: 250, y: 750 }, data: { label: 'Opened Email?', condition_type: 'email_opened' } },
      { id: task, type: 'createTask', position: { x: 0, y: 900 }, data: { label: 'Sales Follow-Up', create_task_config: { task_type: 'call', title_template: 'Call {{contact.full_name}} at {{contact.company_name}}', description_template: 'Prospect opened the personalized deck. Schedule a call to discuss.', priority: 'high', due_in_days: 1 } } },
      { id: followup, type: 'sendEmail', position: { x: 500, y: 900 }, data: { label: 'Follow-Up Email', subject: '', body: '', ai_config: aiEmailConfig('follow_up') } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: enrich, animated: true, type: 'smoothstep' },
      { id: 'e2', source: enrich, target: deck, animated: true, type: 'smoothstep' },
      { id: 'e3', source: deck, target: email, animated: true, type: 'smoothstep' },
      { id: 'e4', source: email, target: delay, animated: true, type: 'smoothstep' },
      { id: 'e5', source: delay, target: cond, animated: true, type: 'smoothstep' },
      { id: 'e6', source: cond, target: task, sourceHandle: 'yes', animated: true, type: 'smoothstep' },
      { id: 'e7', source: cond, target: followup, sourceHandle: 'no', animated: true, type: 'smoothstep' },
    ],
  }
}

// ─── Template 9: Re-Engagement Campaign ──────────────────────────────
function reEngagementCampaign(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const email1 = id('n')
  const delay1 = id('n')
  const cond = id('n')
  const email2 = id('n')
  const tag = id('n')
  const exit = id('n')

  return {
    id: 're_engagement_campaign',
    name: 'Re-Engagement Campaign',
    description: 'Win back cold leads who haven\'t engaged in a while. Sends a "we miss you" email, checks for opens, and either welcomes them back or tags them as cold and exits. Use to clean your list and revive dormant prospects.',
    category: 'nurture',
    icon: Lightning,
    tags: ['win-back', 're-engage', 'list hygiene'],
    flowSummary: 'Trigger → We Miss You → Delay → Opened? → Welcome Back / Tag Cold + Exit',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'Dormant Leads', trigger_type: 'manual' } },
      { id: email1, type: 'sendEmail', position: { x: 250, y: 150 }, data: { label: 'We Miss You', subject: '', body: '', ai_config: aiEmailConfig('re_engage', 'friendly') } },
      { id: delay1, type: 'delay', position: { x: 250, y: 300 }, data: { label: 'Wait 5 Days', delay_value: 5, delay_unit: 'days', delay_mode: 'duration' } },
      { id: cond, type: 'condition', position: { x: 250, y: 450 }, data: { label: 'Opened?', condition_type: 'email_opened' } },
      { id: email2, type: 'sendEmail', position: { x: 0, y: 600 }, data: { label: 'Welcome Back', subject: '', body: '', ai_config: aiEmailConfig('nurture', 'friendly') } },
      { id: tag, type: 'action', position: { x: 500, y: 600 }, data: { label: 'Tag as Cold', action_type: 'tag_contact', action_config: { tag: 'cold_lead' } } },
      { id: exit, type: 'exitWorkflow', position: { x: 500, y: 750 }, data: { label: 'Remove from List', exit_workflow_config: { reason: 'disqualified', add_tag: 'inactive' } } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: email1, animated: true, type: 'smoothstep' },
      { id: 'e2', source: email1, target: delay1, animated: true, type: 'smoothstep' },
      { id: 'e3', source: delay1, target: cond, animated: true, type: 'smoothstep' },
      { id: 'e4', source: cond, target: email2, sourceHandle: 'yes', animated: true, type: 'smoothstep' },
      { id: 'e6', source: cond, target: tag, sourceHandle: 'no', animated: true, type: 'smoothstep' },
      { id: 'e7', source: tag, target: exit, animated: true, type: 'smoothstep' },
    ],
  }
}

// ─── Template 10: AI-Powered Smart Routing ───────────────────────────
// TODO: hidden for now — this feature isn't built yet. Keeping the template
// definition commented out so it isn't shown or fetched. Revisit in future.
/*
function aiSmartRouting(): WorkflowTemplateDef {
  _c = 0
  const trigger = id('t')
  const enrich = id('n')
  const ai = id('n')
  const alert = id('n')
  const assign = id('n')
  const crm = id('n')
  const exit = id('n')
  const email = id('n')
  const delay = id('n')
  const email2 = id('n')

  return {
    id: 'ai_smart_routing',
    name: 'AI-Powered Smart Routing',
    description: 'Let AI analyze each lead and decide the best action. After enrichment, an AI decision node evaluates intent signals and routes to: immediate sales action with CRM sync, nurture sequence, or exit. The future of lead management.',
    category: 'lead_qualification',
    icon: Brain,
    tags: ['AI', 'intelligent', 'routing', 'CRM'],
    flowSummary: 'Trigger → Enrich → AI Decision → Alert+Assign+CRM / Nurture Emails / Exit',
    nodes: [
      { id: trigger, type: 'trigger', position: { x: 250, y: 0 }, data: { label: 'Incoming Lead', trigger_type: 'contact_added' } },
      { id: enrich, type: 'enrichContacts', position: { x: 250, y: 150 }, data: { label: 'Full Enrich', enrich_contacts_config: { skip_already_enriched: true, max_age_days: 0, enabled_blocks: ['person', 'company', 'sector', 'buying_signals', 'outreach_hooks', 'competitive_intel'], providers: [], execution_mode: 'parallel' } } },
      { id: ai, type: 'aiDecision', position: { x: 250, y: 300 }, data: { label: 'AI Evaluation', ai_decision_config: { decision_prompt: 'Based on the contact\'s seniority, company size, buying signals, and industry, determine if this lead is: (1) High intent - ready for sales outreach, (2) Low intent - should be nurtured over time, (3) Poor fit - should be exited from the workflow.', branches: [{ id: 'high', label: 'High Intent', description: 'Ready for direct sales engagement' }, { id: 'nurture', label: 'Needs Nurture', description: 'Interested but not ready to buy' }, { id: 'exit', label: 'Poor Fit', description: 'Not a good match' }], contact_fields: ['job_title', 'company_name', 'company_industry', 'company_size', 'person_enrichment', 'company_enrichment', 'buying_signals'] } } },
      { id: alert, type: 'internalAlert', position: { x: 0, y: 480 }, data: { label: 'Alert Sales', internal_alert_config: { channel: 'email', message_template: 'Hot lead: {{contact.full_name}} ({{contact.job_title}} at {{contact.company_name}}) — AI scored as high intent.', include_contact_details: true } } },
      { id: assign, type: 'assignOwner', position: { x: 0, y: 630 }, data: { label: 'Assign Representative', assign_owner_config: { team_member_emails: [], assignment_mode: 'round_robin' } } },
      { id: crm, type: 'crmSync', position: { x: 0, y: 780 }, data: { label: 'Sync to CRM', crm_sync_config: { provider: 'hubspot', sync_contact: true, create_deal: true, deal_name_template: '{{contact.company_name}} — AI Qualified', deal_stage: 'Qualified', field_mapping: {} } } },
      { id: email, type: 'sendEmail', position: { x: 250, y: 480 }, data: { label: 'Nurture Email 1', subject: '', body: '', ai_config: aiEmailConfig('nurture') } },
      { id: delay, type: 'delay', position: { x: 250, y: 630 }, data: { label: 'Wait 5 Days', delay_value: 5, delay_unit: 'days', delay_mode: 'duration', business_days_only: true } },
      { id: email2, type: 'sendEmail', position: { x: 250, y: 780 }, data: { label: 'Nurture Email 2', subject: '', body: '', ai_config: aiEmailConfig('nurture') } },
      { id: exit, type: 'exitWorkflow', position: { x: 500, y: 480 }, data: { label: 'Not a Fit', exit_workflow_config: { reason: 'wrong_persona', add_tag: 'ai_disqualified' } } },
    ],
    edges: [
      { id: 'e1', source: trigger, target: enrich, animated: true, type: 'smoothstep' },
      { id: 'e2', source: enrich, target: ai, animated: true, type: 'smoothstep' },
      { id: 'e3', source: ai, target: alert, sourceHandle: 'high', animated: true, type: 'smoothstep' },
      { id: 'e4', source: alert, target: assign, animated: true, type: 'smoothstep' },
      { id: 'e5', source: assign, target: crm, animated: true, type: 'smoothstep' },
      { id: 'e6', source: ai, target: email, sourceHandle: 'nurture', animated: true, type: 'smoothstep' },
      { id: 'e7', source: email, target: delay, animated: true, type: 'smoothstep' },
      { id: 'e8', source: delay, target: email2, animated: true, type: 'smoothstep' },
      { id: 'e9', source: ai, target: exit, sourceHandle: 'exit', animated: true, type: 'smoothstep' },
    ],
  }
}
*/

// ─── Export all templates ─────────────────────────────────────────────
export const WORKFLOW_TEMPLATES: WorkflowTemplateDef[] = [
  coldOutreach3Step(),
  warmFollowUp(),
  leadQualificationRouting(),
  nurtureDrip5Touch(),
  eventWebinarInvite(),
  abTestSubjectLines(),
  enrichmentPersonalizedDeck(),
  reEngagementCampaign(),
  // aiSmartRouting(), // TODO: hidden for now — feature not built yet, revisit in future
]
